import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

/** A variable declared on a persona; credentials fill the value (injected as `$var.USER.<name>`). */
export type PersonaVariableDefinition = {
  name: string;
  description?: string;
  sensitive?: boolean;
};

export type CredentialTemplate = {
  id: string;
  orgId: string;
  propertyId: string;
  name: string;
  description: string | null;
  variableDefinitions: PersonaVariableDefinition[] | null;
  createdAt: string;
  updatedAt: string;
};

const orgCredentialsKey = ['org-credentials-with-properties'];
const templatesKey = (propertyId: string) => ['credential-templates', propertyId];
const orgTemplatesKey = ['org-credential-templates'];

export function useOrgCredentialTemplates(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: orgTemplatesKey,
    queryFn: async () => {
      const r = await fetch(`${API_URL}/org/credential-templates`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to fetch credential templates');
      const data = (await r.json()) as { items?: CredentialTemplate[] };
      return data.items ?? [];
    },
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useCredentialTemplates(propertyId: string | null | undefined) {
  return useQuery({
    queryKey: templatesKey(propertyId ?? ''),
    queryFn: async (): Promise<CredentialTemplate[]> => {
      if (!propertyId) return [];
      const r = await fetch(
        `${API_URL}/org/properties/${propertyId}/credential-templates`,
        { credentials: 'include' }
      );
      if (!r.ok) throw await toApiError(r, 'Failed to fetch credential templates');
      const data = await r.json();
      return (data.items || []) as CredentialTemplate[];
    },
    enabled: !!propertyId,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useCreateCredentialTemplate(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string | null;
      variableDefinitions?: PersonaVariableDefinition[];
    }) => {
      const r = await fetch(`${API_URL}/org/properties/${propertyId}/credential-templates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: input.name,
          description: input.description ?? null,
          ...(input.variableDefinitions !== undefined
            ? { variableDefinitions: input.variableDefinitions }
            : {}),
        }),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to create template');
      const data = await r.json();
      return data.template as CredentialTemplate;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templatesKey(propertyId) });
    },
  });
}

export function useUpdateCredentialTemplate(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      templateId: string;
      name?: string;
      description?: string | null;
      variableDefinitions?: PersonaVariableDefinition[];
      /** Bypass the delete-safety check when removing an in-use variable. */
      force?: boolean;
    }) => {
      const r = await fetch(
        `${API_URL}/org/properties/${propertyId}/credential-templates/${input.templateId}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            ...(input.name !== undefined ? { name: input.name } : {}),
            ...(input.description !== undefined ? { description: input.description } : {}),
            ...(input.variableDefinitions !== undefined
              ? { variableDefinitions: input.variableDefinitions }
              : {}),
            ...(input.force ? { force: true } : {}),
          }),
        }
      );
      if (!r.ok) throw await toApiError(r, 'Failed to update template');
      return (await r.json()).template as CredentialTemplate;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templatesKey(propertyId) });
    },
  });
}

export function useDeleteCredentialTemplate(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      const r = await fetch(
        `${API_URL}/org/properties/${propertyId}/credential-templates/${templateId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!r.ok) throw await toApiError(r, 'Failed to delete template');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templatesKey(propertyId) });
      qc.invalidateQueries({ queryKey: orgCredentialsKey });
    },
  });
}

export function useLinkCredentialToTemplate(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { templateId: string; credentialId: string }) => {
      const r = await fetch(
        `${API_URL}/org/properties/${propertyId}/credential-templates/${input.templateId}/bindings`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ credentialId: input.credentialId }),
        }
      );
      if (!r.ok) throw await toApiError(r, 'Failed to link credential to template');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgCredentialsKey });
      qc.invalidateQueries({ queryKey: templatesKey(propertyId) });
    },
  });
}

export function useUnlinkCredentialFromTemplate(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { templateId: string; credentialId: string }) => {
      const r = await fetch(
        `${API_URL}/org/properties/${propertyId}/credential-templates/${input.templateId}/bindings/${input.credentialId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!r.ok) throw await toApiError(r, 'Failed to unlink credential');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgCredentialsKey });
      qc.invalidateQueries({ queryKey: templatesKey(propertyId) });
    },
  });
}
